import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useListaFiltrada } from "@/lib/filtros";
import { type FiltrosCandados, filtrarCandados } from "@/lib/filtros/candados";

/**
 * EstadoCandado es el estado operativo en tiempo real de un candado. Hoy siempre es
 * "LIBRE"; "EN_USO" queda cableado para que F3/#37 lo encienda cuando el candado esté
 * colgado en un bloqueo LOTO activo (ver backend factory/candado_repository.go).
 */
export type EstadoCandado = "LIBRE" | "EN_USO";

/**
 * PuntoDeCandado es uno de los puntos de aislamiento que el candado cubre AHORA (#248),
 * con el equipo del punto para poder situarlo sin abrir el LOTO.
 */
export type PuntoDeCandado = {
  id_punto: number;
  nombre: string;
  id_equipo: number;
  codigo_equipo: string;
};

/**
 * LotoDeCandado es el LOTO del bloqueo ACTIVO del candado (#248). Es null cuando el
 * candado está libre: sin bloqueo vivo no hay LOTO al que enlazar.
 */
export type LotoDeCandado = {
  id_loto: number;
  numero_loto: string;
};

/**
 * Candado es un candado físico del catálogo (GET /api/v1/candados). `codigo_candado`
 * es la clave de negocio visible; `estado`/`num_usos` derivan de la operación LOTO
 * (#243). `puntos` y `loto` dicen DÓNDE está puesto (#248): `[]` y null si está libre.
 */
export type Candado = {
  id: number;
  codigo_candado: string;
  descripcion: string | null;
  activo: boolean;
  estado: EstadoCandado;
  num_usos: number;
  puntos: PuntoDeCandado[];
  loto: LotoDeCandado | null;
  version: number;
  updated_at: string;
};

/** CandadoInput es el payload de alta/edición (POST/PUT /api/v1/candados). */
export type CandadoInput = {
  codigo_candado: string;
  descripcion: string | null;
  activo: boolean;
};

/**
 * candadosQueryKey namespacea la caché de candados POR obra: `["obra", obraId, "candados"]`
 * (F7 #158). El `codigo_candado` es único POR obra (repetible entre obras), así que la
 * caché debe aislarse por obra para no mezclar catálogos.
 */
export function candadosQueryKey(obraId: number) {
  return ["obra", obraId, "candados"] as const;
}

/**
 * candadosQueryOptions lista los candados vivos con su estado de la obra `obraId`. Se
 * refresca invalidando la clave tras cada mutación (create/update/delete), igual que el
 * maestro de áreas, ahora con la obra en la ruta y en la clave.
 */
export function candadosQueryOptions(obraId: number) {
  return queryOptions({
    queryKey: candadosQueryKey(obraId),
    queryFn: () => api.get<Candado[]>(`/obras/${obraId}/candados`),
  });
}

/**
 * useCandados lista los candados de la obra, opcionalmente FILTRADOS (#247): con red filtra
 * el servidor; sin red, la función pura sobre la lista base cacheada.
 */
export function useCandados(obraId: number, filtros: FiltrosCandados = {}) {
  return useListaFiltrada<Candado>({
    claveBase: candadosQueryKey(obraId),
    url: `/obras/${obraId}/candados`,
    filtros,
    filtrar: filtrarCandados,
  });
}

export function useCreateCandado(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CandadoInput) => api.post<Candado>(`/obras/${obraId}/candados`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: candadosQueryKey(obraId) }),
  });
}

export function useUpdateCandado(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: CandadoInput }) =>
      api.put<Candado>(`/obras/${obraId}/candados/${id}`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: candadosQueryKey(obraId) }),
  });
}

export function useDeleteCandado(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del<void>(`/obras/${obraId}/candados/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: candadosQueryKey(obraId) }),
  });
}
